import { useMemo, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import {
  Outlet,
  useLoaderData,
  useLocation,
  useNavigate,
  useRevalidator,
  useSearchParams,
  type LoaderFunctionArgs,
} from 'react-router-dom';
import {
  Button,
  cn,
  EmptyState,
  Input,
  Modal,
  StatusBadge,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import type {
  BackofficeCredentialAdminItem,
  BackofficeCredentialItem,
  McpAccessSwitchResponse,
  McpToolExposureFilter,
  McpToolExposureFilterCounts,
  McpToolExposureItem,
  McpToolModuleFacet,
  WorkspaceMcpSwitchResponse,
} from '@immo/api-contract';
import { McpToolExposureFilterSchema, mcpToolIsCatalogued } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { FilterPill } from '@/components/filter-bar';
import { ToolReachabilityPanel } from '@/components/mcp/tool-reachability-panel';
import { RequirePermission, useCan } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { ColumnLabel, ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import {
  ChevronDownIcon,
  ClockIcon,
  KeyIcon,
  PlusIcon,
  ShieldIcon,
  TagIcon,
  TextIcon,
} from '@/components/layout/icons';
import { ARIA_SORT, SortableHeader, useUrlTableSort, type SortValue } from '@/components/sortable-table';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';

type ToolsLoad =
  | {
      readonly kind: 'ready';
      readonly items: ReadonlyArray<McpToolExposureItem>;
      // What the current narrowing matches BEFORE paging, and every module in
      // it — both computed by the server (IMM-769.7), because the page only
      // ever holds the slice it asked for and cannot count what it never saw.
      readonly total: number;
      // How many of that same narrowing are exposed (IMM-832.1) — the server's
      // count, over exactly the population `total` describes.
      readonly exposedTotal: number;
      readonly modules: ReadonlyArray<McpToolModuleFacet>;
      // What each chip would return under the current search term — so the
      // triage backlog is readable without selecting it, which is the whole
      // point of having built a triage queue.
      readonly filters: McpToolExposureFilterCounts;
    }
  | { readonly kind: 'failed' };

type LoaderData = {
  readonly credentials: ReadonlyArray<BackofficeCredentialItem>;
  readonly accessSwitch: McpAccessSwitchResponse;
  // Null when the caller lacks the manage grant — the backend refuses the
  // platform-wide list with 403, so the data is absent from the payload, not
  // merely hidden by the UI.
  readonly allCredentials: ReadonlyArray<BackofficeCredentialAdminItem> | null;
  // Null when the caller lacks `backoffice:identity:mcp-surface-read` — the
  // backend refuses the leaf with 403, so the customer-surface section is absent
  // from the payload rather than hidden by the UI (IMM-601.6).
  readonly customerSwitch: WorkspaceMcpSwitchResponse | null;
  // Null ONLY when the caller lacks `backoffice:identity:mcp-surface-read` —
  // the backend refuses the leaf with 403, so the section is absent from the
  // payload rather than hidden by the UI (IMM-625.2 AC 7).
  //
  // A read that failed for any OTHER reason is `{ kind: 'failed' }`, not null:
  // collapsing the two would make a 5xx or a dropped connection read as a
  // permission boundary, and an operator mid-incident would conclude they lack
  // a grant they actually hold (BL-058).
  readonly tools: ToolsLoad | null;
  // Which slice of the catalogue the loader ASKED for, echoed back so the chips
  // and the empty state describe the request that produced `tools` rather than
  // whatever the URL says at render time.
  readonly toolFilter: McpToolExposureFilter;
  // Echoed back for the same reason `toolFilter` is: the controls must describe
  // the request that produced `tools`, not whatever the URL says at render time.
  readonly toolSearch: string;
  readonly toolModule: string | null;
  readonly toolOffset: number;
  readonly columnPreference: ColumnPreference;
};

/**
 * The tool-list filter lives in the URL, not in component state (IMM-625.3).
 *
 * `unreviewed` carries an ORDER — newest arrival first — that only the server
 * can answer, so the filter has to reach the loader; and a loader is re-run by
 * navigation, which means the search param is the only place the choice can
 * live. It is also the reason the choice is never seeded into `useState`
 * (BL-068): it is derived from the URL on every render, by both the loader and
 * the chips.
 */
const TOOL_FILTER_PARAM = 'tool-filter';
const DEFAULT_TOOL_FILTER: McpToolExposureFilter = 'all';

/**
 * The rest of the narrowing, in the URL for the same reason the filter is
 * (IMM-769.7): the server answers all of it now, so a shared or reloaded URL
 * has to reproduce the list an operator is looking at — including the search
 * term, which used to be the one narrowing that lived in component state.
 */
const TOOL_SEARCH_PARAM = 'tool-q';
const TOOL_MODULE_PARAM = 'tool-module';
const TOOL_OFFSET_PARAM = 'tool-offset';

export const mcpAccessLoaderParams = [
  TOOL_FILTER_PARAM,
  TOOL_SEARCH_PARAM,
  TOOL_MODULE_PARAM,
  TOOL_OFFSET_PARAM,
] as const;

/**
 * One page of the catalogue. Fixed rather than a control: a page size an
 * operator can change is a preference to store and a number to explain, and
 * nobody has asked for either.
 */
const TOOL_PAGE_SIZE = 50;

/** A non-negative integer, or 0 — a hand-edited offset narrows nothing rather than 400ing the loader. */
function offsetFromParams(searchParams: URLSearchParams): number {
  const parsed = Number(searchParams.get(TOOL_OFFSET_PARAM));

  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * Anything the contract does not recognise falls back to `all`, so a
 * hand-edited or stale URL narrows nothing rather than sending an unvalidated
 * string to the backend.
 */
function toolFilterFromParams(searchParams: URLSearchParams): McpToolExposureFilter {
  const parsed = McpToolExposureFilterSchema.safeParse(searchParams.get(TOOL_FILTER_PARAM));

  return parsed.success ? parsed.data : DEFAULT_TOOL_FILTER;
}

/**
 * `MCP-Zugang` (IMM-522.1) — the operator's own read-only, cross-tenant
 * credentials for MCP clients.
 *
 * The backend scopes the list to the caller, so this page never filters by
 * operator: another operator's credentials do not reach the browser at all. A
 * 403 here (an operator without the read permission hitting the URL directly)
 * propagates as a route error and lands on `/access-denied`.
 */
export async function mcpAccessLoader({ request }: LoaderFunctionArgs): Promise<LoaderData> {
  const searchParams = new URL(request.url).searchParams;
  const toolFilter = toolFilterFromParams(searchParams);
  const toolSearch = (searchParams.get(TOOL_SEARCH_PARAM) ?? '').trim();
  const toolModule = searchParams.get(TOOL_MODULE_PARAM);
  const toolOffset = offsetFromParams(searchParams);

  const [result, switchResult, allResult, customerSwitchResult, toolsResult, columnPreference] = await Promise.all([
    apiClient.adminMcpAccess.list(),
    apiClient.adminMcpAccess.getSwitch(),
    apiClient.adminMcpAccess.listAll(),
    apiClient.adminMcpSurface.getSwitch(),
    apiClient.adminMcpSurface.listTools({
      query: {
        filter: toolFilter,
        // Omitted rather than sent empty: the contract's `min(1)` refuses a
        // blank term, and "no term" is the absence of the parameter.
        ...(toolSearch === '' ? {} : { q: toolSearch }),
        ...(toolModule === null || toolModule === '' ? {} : { module: toolModule }),
        limit: TOOL_PAGE_SIZE,
        offset: toolOffset,
      },
    }),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.mcpAccess),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load MCP accesses', { status: result.status });
  }

  if (!switchResult.ok) {
    throw new Response('Failed to load the MCP access switch', { status: switchResult.status });
  }

  return {
    credentials: result.body.credentials,
    accessSwitch: switchResult.body,
    allCredentials: allResult.ok ? allResult.body.credentials : null,
    customerSwitch: customerSwitchResult.ok ? customerSwitchResult.body : null,
    tools: toolsResult.ok
      ? {
          kind: 'ready',
          items: toolsResult.body.items,
          total: toolsResult.body.total,
          exposedTotal: toolsResult.body.exposedTotal,
          modules: toolsResult.body.modules,
          filters: toolsResult.body.filters,
        }
      : toolsResult.status === 403
        ? null
        : { kind: 'failed' },
    toolFilter,
    toolSearch,
    toolModule: toolModule === null || toolModule === '' ? null : toolModule,
    toolOffset,
    columnPreference,
  };
}

type AllCredentialField = 'name' | 'issuedBy' | 'status' | 'expiresAt';
const ALL_CREDENTIAL_FIELDS: ReadonlyArray<AllCredentialField> = ['name', 'issuedBy', 'status', 'expiresAt'];
type AllCredentialRow = BackofficeCredentialAdminItem & { readonly statusLabel: string };

function issuedByLabel(credential: BackofficeCredentialAdminItem): string {
  return credential.issuedByAdminEmail ?? credential.issuedByAdminId;
}

function allCredentialSortKey(row: AllCredentialRow, field: AllCredentialField): SortValue {
  switch (field) {
    case 'name':
      return row.name;
    case 'issuedBy':
      return issuedByLabel(row);
    case 'status':
      return row.statusLabel;
    case 'expiresAt':
      return Date.parse(row.expiresAt);
  }
}

function useMcpAccessColumns(): ReadonlyArray<OperatorColumn<BackofficeCredentialItem>> {
  const t = useTranslations('pages.admin.mcp-access');
  const intl = useIntl();

  return useMemo(() => {
    const formatTimestamp = (value: string | null): string =>
      value === null ? t('never') : intl.formatDate(value, { dateStyle: 'short', timeStyle: 'short' });

    return [
      {
        id: 'name',
        label: t('columns.name'),
        icon: TextIcon,
        cell: (credential) => credential.name,
        sortValue: (credential) => credential.name,
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TagIcon,
        cell: (credential) => t(`status.${credential.status}`),
        sortValue: (credential) => t(`status.${credential.status}`),
      },
      {
        id: 'expiresAt',
        label: t('columns.expires-at'),
        icon: ClockIcon,
        cellClassName: 'text-text-tertiary tabular-nums',
        cell: (credential) => formatTimestamp(credential.expiresAt),
        sortValue: (credential) => Date.parse(credential.expiresAt),
      },
      {
        id: 'createdAt',
        label: t('columns.created-at'),
        icon: ClockIcon,
        cellClassName: 'text-text-tertiary tabular-nums',
        cell: (credential) => formatTimestamp(credential.createdAt),
        sortValue: (credential) => Date.parse(credential.createdAt),
      },
      {
        id: 'lastUsed',
        label: t('columns.last-used'),
        icon: ShieldIcon,
        cellClassName: 'text-text-tertiary tabular-nums',
        cell: (credential) => formatTimestamp(credential.lastUsedAt),
        sortValue: (credential) => (credential.lastUsedAt === null ? null : Date.parse(credential.lastUsedAt)),
      },
    ];
  }, [t, intl]);
}

export function McpAccessPage() {
  const t = useTranslations('pages.admin.mcp-access');
  const tRevoke = useTranslations('pages.admin.mcp-access.revoke');
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const revalidator = useRevalidator();
  const {
    credentials,
    accessSwitch,
    allCredentials,
    customerSwitch,
    tools,
    toolFilter,
    toolSearch,
    toolModule,
    toolOffset,
    columnPreference,
  } = useLoaderData() as LoaderData;
  const allCredentialRows = useMemo<ReadonlyArray<AllCredentialRow>>(
    () =>
      (allCredentials ?? []).map((credential) => ({ ...credential, statusLabel: t(`status.${credential.status}`) })),
    [allCredentials, t],
  );
  // The page's second table sorts under its own `all-` keys, beside the credential table's `sort`/`dir` (IMM-1075).
  const allSort = useUrlTableSort(allCredentialRows, allCredentialSortKey, {
    fields: ALL_CREDENTIAL_FIELDS,
    paramPrefix: 'all-',
  });
  const catalogue = useMcpAccessColumns();
  const table = useOperatorTable({
    rows: credentials,
    preferenceKey: OPERATOR_COLUMN_KEYS.mcpAccess,
    catalogue,
    alwaysVisibleId: 'name',
    loadedPreference: columnPreference,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const tIncident = useTranslations('pages.admin.mcp-access.incident');
  const tCustomer = useTranslations('pages.admin.mcp-access.customer-surface');
  const tTools = useTranslations('pages.admin.mcp-access.tools');
  const tTable = useTranslations('pages.admin.shell.table');
  const tReach = useTranslations('pages.admin.mcp-access.tools.reachability');

  const [pendingForeignRevoke, setPendingForeignRevoke] = useState<BackofficeCredentialAdminItem | null>(null);
  const [pendingSwitch, setPendingSwitch] = useState<boolean | null>(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const [pendingCustomerSwitch, setPendingCustomerSwitch] = useState<boolean | null>(null);
  const [customerSwitching, setCustomerSwitching] = useState(false);
  const [customerSwitchError, setCustomerSwitchError] = useState<string | null>(null);

  // The search box's DRAFT, seeded from the URL rather than from the loader
  // (IMM-769.7). The distinction is the one BL-068 is about: seeding a control
  // from data that arrives later leaves it describing something it has not
  // seen, whereas the URL is the request itself and is known synchronously on
  // the first render. The narrowing is the server's now, so the value the
  // operator sees while typing is local and the rows are always the loader's.
  const [searchDraft, setSearchDraft] = useState(() => searchParams.get(TOOL_SEARCH_PARAM) ?? '');

  // Which tools the operator has picked for a batch ruling. Cleared by every
  // narrowing change: a selection that survived a filter change would rule on
  // rows that are no longer on screen, which is the one thing an all-or-nothing
  // write must never do.
  const [selectedToolIds, setSelectedToolIds] = useState<ReadonlySet<string>>(new Set());
  const [pendingBulk, setPendingBulk] = useState<boolean | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const [pendingWithdraw, setPendingWithdraw] = useState<McpToolExposureItem | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<McpToolExposureItem | null>(null);
  const [entryRemoved, setEntryRemoved] = useState(false);
  // Which row is expanded — its full metadata plus its diagnosis panel
  // (IMM-625.4). One at a time: two open verdicts about two different tools is
  // exactly the confusion the panel exists to remove, and support reads one
  // answer at a time.
  const [expandedToolId, setExpandedToolId] = useState<string | null>(null);
  // Which expanded row has ALSO asked for its diagnosis. A second piece of
  // state rather than a flag on the row: the panel fetches the credential lists
  // on mount, so „the row is open" and „the operator wants the verdict" have to
  // be separate facts or reading a description costs a cross-workspace read.
  const [diagnosedToolId, setDiagnosedToolId] = useState<string | null>(null);
  // The tools whose own write is in flight. A SET rather than a single id: two
  // rows can be saved at once, and with one id the first to settle would clear
  // the flag and re-enable the second row's button mid-request.
  const [savingToolIds, setSavingToolIds] = useState<ReadonlySet<string>>(new Set());
  // The switch carries the exposure state for a manager; a read-only operator
  // has no switch, so the badge is the only place the state is shown.
  const canManageSurface = useCan('backoffice:identity:mcp-surface-manage');
  const [toolError, setToolError] = useState<string | null>(null);

  const [pendingRevoke, setPendingRevoke] = useState<BackofficeCredentialItem | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const closeConfirm = () => {
    if (revoking) return;

    setPendingRevoke(null);
    setRevokeError(null);
  };

  const confirmRevoke = async () => {
    if (pendingRevoke === null) return;

    setRevoking(true);
    setRevokeError(null);

    try {
      const result = await apiClient.adminMcpAccess.revoke({ pathParams: { id: pendingRevoke.id }, body: {} });

      if (result.ok) {
        setPendingRevoke(null);
        revalidator.revalidate();

        return;
      }

      setRevokeError(tRevoke('generic-error'));
    } catch {
      setRevokeError(tRevoke('generic-error'));
    } finally {
      setRevoking(false);
    }
  };

  const confirmForeignRevoke = async () => {
    if (pendingForeignRevoke === null) return;

    setRevoking(true);
    setRevokeError(null);

    try {
      const result = await apiClient.adminMcpAccess.revokeAny({
        pathParams: { id: pendingForeignRevoke.id },
        body: {},
      });

      if (result.ok) {
        setPendingForeignRevoke(null);
        revalidator.revalidate();

        return;
      }

      setRevokeError(tRevoke('generic-error'));
    } catch {
      setRevokeError(tRevoke('generic-error'));
    } finally {
      setRevoking(false);
    }
  };

  const confirmSwitch = async () => {
    if (pendingSwitch === null) return;

    setSwitching(true);
    setSwitchError(null);

    try {
      const result = await apiClient.adminMcpAccess.setSwitch({ body: { enabled: pendingSwitch } });

      if (result.ok) {
        setPendingSwitch(null);
        revalidator.revalidate();

        return;
      }

      setSwitchError(tIncident('switch-error'));
    } catch {
      setSwitchError(tIncident('switch-error'));
    } finally {
      setSwitching(false);
    }
  };

  const confirmCustomerSwitch = async () => {
    if (pendingCustomerSwitch === null) return;

    setCustomerSwitching(true);
    setCustomerSwitchError(null);

    try {
      const result = await apiClient.adminMcpSurface.setSwitch({ body: { enabled: pendingCustomerSwitch } });

      if (result.ok) {
        setPendingCustomerSwitch(null);
        revalidator.revalidate();

        return;
      }

      setCustomerSwitchError(tCustomer('switch-error'));
    } catch {
      setCustomerSwitchError(tCustomer('switch-error'));
    } finally {
      setCustomerSwitching(false);
    }
  };

  /**
   * The exposure write is deliberately NOT optimistic (IMM-625.2 AC 6).
   *
   * Nothing here touches a local copy of the row: the table always renders the
   * loader's `tools`, and a success revalidates so the loader becomes the
   * source of truth again. A failure therefore leaves the row showing the state
   * the platform is still enforcing — a row that sticks in the new state after
   * a failed write puts an operator in front of a screen that disagrees with
   * production during the exact incident this page exists for.
   */
  const saveToolExposure = async (tool: McpToolExposureItem, exposed: boolean) => {
    setSavingToolIds((current) => new Set(current).add(tool.toolId));
    setToolError(null);
    setEntryRemoved(false);

    try {
      const result = await apiClient.adminMcpSurface.setToolExposure({
        pathParams: { toolId: tool.toolId },
        body: { exposed },
      });

      if (result.ok) {
        setPendingWithdraw(null);
        revalidator.revalidate();

        return;
      }

      setToolError(tTools('save-error'));
    } catch {
      setToolError(tTools('save-error'));
    } finally {
      setSavingToolIds((current) => {
        const next = new Set(current);

        next.delete(tool.toolId);

        return next;
      });
    }
  };

  /**
   * Clearing a stale entry, on the same non-optimistic terms as the exposure
   * write above: nothing is removed locally, the success revalidates, and a
   * failure leaves the row exactly where the platform still has it.
   *
   * It shares `savingToolIds` with `saveToolExposure` deliberately — an operator
   * may act on two rows at once, and the two writes are the same kind of
   * per-row, in-flight fact.
   */
  const removeStaleEntry = async (tool: McpToolExposureItem) => {
    setSavingToolIds((current) => new Set(current).add(tool.toolId));
    setToolError(null);
    setEntryRemoved(false);

    try {
      const result = await apiClient.adminMcpSurface.removeToolExposure({ pathParams: { toolId: tool.toolId } });

      if (result.ok) {
        setPendingRemoval(null);
        setEntryRemoved(true);
        revalidator.revalidate();

        return;
      }

      setToolError(tTools('stale.remove-error'));
    } catch {
      setToolError(tTools('stale.remove-error'));
    } finally {
      setSavingToolIds((current) => {
        const next = new Set(current);

        next.delete(tool.toolId);

        return next;
      });
    }
  };

  /**
   * The ONE writer of the tool list's URL state (IMM-769.7).
   *
   * Every narrowing change goes through here so that two things can never be
   * forgotten: the offset returns to the first page, and the selection is
   * dropped. A selection that outlived its narrowing would let an
   * all-or-nothing ruling land on rows the operator can no longer see.
   */
  const narrowTools = (mutate: (params: URLSearchParams) => void, { keepOffset = false } = {}) => {
    setToolError(null);
    setEntryRemoved(false);
    setBulkError(null);

    // Always, paging included. The selection is page-scoped on purpose: the
    // batch bar states a count, and a selection carried across pages would
    // make that count describe less than the write actually names.
    setSelectedToolIds(new Set());

    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);

      mutate(params);

      // Paging deeper keeps its own offset; every other change resets it,
      // because page 4 of a list that just got shorter is a blank screen.
      if (!keepOffset) params.delete(TOOL_OFFSET_PARAM);

      return params;
    });
  };

  /**
   * The batch ruling (IMM-769.7).
   *
   * Non-optimistic, exactly like the single write beside it: nothing touches a
   * local copy of a row, a success revalidates so the loader is the source of
   * truth again, and a failure leaves every row showing the state the platform
   * is still enforcing. The backend is all-or-nothing, so there is no partial
   * result for this to have to render.
   */
  const saveBulkExposure = async (exposed: boolean) => {
    const toolIds = [...selectedToolIds];

    if (toolIds.length === 0) return;

    setBulkSaving(true);
    setBulkError(null);
    setEntryRemoved(false);

    try {
      const result = await apiClient.adminMcpSurface.setToolExposureBatch({ body: { toolIds, exposed } });

      if (result.ok) {
        setPendingBulk(null);
        setSelectedToolIds(new Set());
        revalidator.revalidate();

        return;
      }

      setBulkError(tTools('bulk.error'));
    } catch {
      setBulkError(tTools('bulk.error'));
    } finally {
      setBulkSaving(false);
    }
  };

  const selectToolFilter = (next: McpToolExposureFilter) => {
    narrowTools((params) => {
      // `all` is the default, so it is expressed by the ABSENCE of the param —
      // a shared URL then carries a filter only when one was actually chosen.
      if (next === DEFAULT_TOOL_FILTER) params.delete(TOOL_FILTER_PARAM);
      else params.set(TOOL_FILTER_PARAM, next);
    });
  };

  // The ONE collection this section reads — its empty branch and its body must
  // never diverge (BL-053). Since IMM-769.7 it is exactly what the server
  // returned: the filter, the search term, the module and the page are all the
  // server's, so there is no client-side narrowing left to disagree with it.
  const visibleTools = tools !== null && tools.kind === 'ready' ? tools.items : [];
  const toolsTotal = tools !== null && tools.kind === 'ready' ? tools.total : 0;
  // The server's count over the same narrowing as `toolsTotal` (IMM-832.1).
  // Never re-derived from `visibleTools`: that is one page, and the label beside
  // it reads as scoped to the whole narrowing.
  const toolsExposedTotal = tools !== null && tools.kind === 'ready' ? tools.exposedTotal : 0;
  const toolModules = tools !== null && tools.kind === 'ready' ? tools.modules : [];
  const toolFilterCounts = tools !== null && tools.kind === 'ready' ? tools.filters : null;
  const isNarrowed = toolSearch !== '' || toolModule !== null || toolFilter !== DEFAULT_TOOL_FILTER;

  // Does the withdrawing batch actually withdraw anything? (IMM-774.1)
  //
  // When nothing in the selection is exposed, the write is the batch form of
  // „Geprüft — nicht freigeben": it takes no access away from anyone and only
  // records the ruling. Calling that „Freigabe entziehen" would describe
  // withdrawing a Freigabe that was never granted, so the bar and its
  // confirmation say what the write does instead.
  //
  // Stated over the SELECTION — every id the write will name must be resolvable
  // in the rendered rows AND unexposed — rather than as „no rendered row is both
  // selected and exposed". The two read differently for an id that is still
  // selected but no longer on screen, which `saveToolExposure` produces every
  // time a ruling on a selected row revalidates it out of a narrowed list. The
  // negative form calls that id harmless and fronts a genuine withdrawal with
  // reassuring copy; this form cannot see it and falls back to the truthful
  // („Freigabe entziehen") wording, which is the safe direction to be wrong in.
  const selectionTakesNothingAway =
    selectedToolIds.size > 0 &&
    [...selectedToolIds].every((toolId) =>
      visibleTools.some((tool) => tool.toolId === toolId && mcpToolIsCatalogued(tool.status) && !tool.exposed),
    );

  // The mirror of `selectionTakesNothingAway`, on the other side of the write
  // (IMM-832.2) — but NOT the mirror of its treatment. „Geprüft — nicht
  // freigeben" stays pressable because that write still records a ruling.
  // Since IMM-1094.6 a tool arrives exposed and unreviewed, so a bulk
  // „Freigeben" over such a row is the operator's review: it records `reviewed`
  // and clears „Neu — noch nicht geprüft" (BL-101). Only a selection that is
  // exposed AND reviewed records nothing — all it would do is overwrite
  // `changedAt`/`changedByAdminId` and publish one changed event per tool for a
  // decision nobody made — so only that one disables the control, rather than
  // relabelling it.
  //
  // Stated over the SELECTION for the same reason that one is: an id still
  // selected but no longer rendered — which `saveToolExposure` produces every
  // time a ruling revalidates a row out of a narrowed list — makes this false,
  // so the bar falls back to offering the write. That is the safe direction:
  // offering a real exposure costs a confirmation, suppressing one costs the
  // operator the action they came for.
  const selectionExposesNothing =
    selectedToolIds.size > 0 &&
    [...selectedToolIds].every((toolId) =>
      visibleTools.some(
        (tool) => tool.toolId === toolId && mcpToolIsCatalogued(tool.status) && tool.exposed && tool.reviewed,
      ),
    );

  const toggleToolSelected = (toolId: string) => {
    setBulkError(null);
    setSelectedToolIds((current) => {
      const next = new Set(current);

      if (next.has(toolId)) next.delete(toolId);
      else next.add(toolId);

      return next;
    });
  };

  /**
   * Which emptiness this is. The search is the one narrowing the page can see
   * through: when the server DID return rows, they were hidden here, so the
   * copy blames the filters. Otherwise the server returned nothing for the
   * chosen slice, and the slice picks the copy — „Alle Werkzeuge sind geprüft."
   * for the triage list (AC 13), the stale one for stale, and the
   * catalogue-is-empty copy only when nothing was narrowed at all.
   */
  const toolsEmptyCopy: 'empty' | 'empty-filtered' | 'empty-unreviewed' | 'empty-stale' =
    toolFilter === 'unreviewed'
      ? 'empty-unreviewed'
      : toolFilter === 'stale'
        ? 'empty-stale'
        : isNarrowed
          ? 'empty-filtered'
          : 'empty';

  /**
   * The customer surface's state and its kill switch, as one element.
   *
   * A local component rather than a duplicated block: it renders in the tool
   * list's header when that list is present, and in a card of its own when the
   * list was refused — and those two must never drift, because between them
   * they are the only place an operator can see whether the customer surface is
   * up at all.
   */
  const CustomerSurfaceState = ({ state }: { readonly state: WorkspaceMcpSwitchResponse }) => (
    <div role="group" aria-label={tCustomer('heading')} className="flex shrink-0 items-center gap-3 text-right">
      <div>
        <p
          className={`flex items-center justify-end gap-1.5 text-base-sm font-medium ${
            state.enabled ? 'text-success-700' : 'text-danger-700'
          }`}
        >
          <span
            aria-hidden
            className={`h-1.5 w-1.5 rounded-full ${state.enabled ? 'bg-success-500' : 'bg-danger-500'}`}
          />
          {tCustomer('heading')}
        </p>
        <p className="text-xs text-text-tertiary">
          {state.enabled ? tCustomer('state-enabled') : tCustomer('state-disabled')}
        </p>
      </div>
      {/* Absent, not visible-and-denied: an operator without the manage grant
          never sees a control they cannot use (PRD AC 59). */}
      <RequirePermission permission="backoffice:identity:mcp-surface-manage">
        {state.enabled ? (
          <Button size="sm" variant="danger-quiet" onClick={() => setPendingCustomerSwitch(false)}>
            {tCustomer('disable-cta')}
          </Button>
        ) : (
          <Button size="sm" onClick={() => setPendingCustomerSwitch(true)}>
            {tCustomer('enable-cta')}
          </Button>
        )}
      </RequirePermission>
    </div>
  );

  const allCredentialHeads: ReadonlyArray<{
    readonly field: AllCredentialField;
    readonly label: string;
    readonly icon: typeof TextIcon;
  }> = [
    { field: 'name', label: t('columns.name'), icon: TextIcon },
    { field: 'issuedBy', label: tIncident('issued-by'), icon: ShieldIcon },
    { field: 'status', label: t('columns.status'), icon: TagIcon },
    { field: 'expiresAt', label: t('columns.expires-at'), icon: ClockIcon },
  ];

  const formatTimestamp = (value: string | null): string =>
    value === null ? t('never') : intl.formatDate(value, { dateStyle: 'short', timeStyle: 'short' });

  return (
    <>
      <PageTopbar
        leading={<ListLeading title={t('title')} count={credentials.length} icon={KeyIcon} />}
        trailing={
          <RequirePermission permission="backoffice:identity:mcp-access-create">
            <Button size="sm" onClick={() => navigate({ pathname: '/admin/mcp-access/new', search: location.search })}>
              <PlusIcon className="h-4 w-4" />
              {t('issue.cta')}
            </Button>
          </RequirePermission>
        }
      />
      {/* Four independent features accumulated on this route across two PRDs and
          sat edge-to-edge on one white surface, separated by hairlines — so
          nothing on the page was a unit an operator could point at. Each is now
          a card on the canvas. Deliberately NO `overflow-hidden` on the cards:
          the tool list's filter bar is `sticky`, and an `overflow` ancestor
          silently kills that. */}
      <div className="flex flex-col gap-4 bg-canvas p-4">
        <p className="rounded-lg border border-border bg-surface px-4 py-3 text-base-sm text-text-secondary">
          {t('description')}
        </p>
        {!accessSwitch.enabled && (
          <p
            role="alert"
            className="rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-base-sm font-medium text-danger-700"
          >
            {tIncident('disabled-banner')}
          </p>
        )}
        {/* Stated ONCE, at the page's head, beside the operator surface's own
            banner — the page has two independently switchable surfaces and the
            reader needs to see WHICH one is down without hunting for the
            section it belongs to. It used to live inside the tool list, which
            meant it disappeared with that section on a refused read: the one
            moment it most needs to be visible.

            AC 9 is unaffected. The reason an EXPOSED tool still does nothing is
            `tools.kill-switch-notice`, which stays inside the list, beside the
            exposure it qualifies. */}
        {customerSwitch !== null && !customerSwitch.enabled && (
          <p
            role="alert"
            className="rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-base-sm font-medium text-danger-700"
          >
            {tCustomer('disabled-banner')}
          </p>
        )}
        {credentials.length === 0 ? (
          // One row, not an `EmptyState` stage. The full stage is right for a page
          // that shows nothing else; here it is the FIRST of four sections, and a
          // ~330px stage for the least-used one pushes the tool list — the reason
          // the page is open — below the fold.
          <div className="flex items-center gap-3 rounded-lg border border-border bg-surface px-4 py-3">
            <KeyIcon className="h-4 w-4 shrink-0 text-text-placeholder" aria-hidden />
            <p className="text-base-sm text-text-secondary">
              <span className="font-semibold text-text-primary">{t('empty.heading')}</span>
              {' — '}
              <span className="text-text-tertiary">{t('empty.description')}</span>
            </p>
          </div>
        ) : (
          <>
            <div className="flex justify-end">
              <ColumnsTrigger {...table.columns.trigger} />
            </div>
            <div className="rounded-lg border border-border bg-surface">
              <Table flush>
                <TableHeader>
                  <TableRow {...table.columns.headerRowProps}>
                    {table.columns.visibleColumns.map((column) => (
                      <OperatorColumnHead key={column.id} column={column} table={table} />
                    ))}
                    <TableHead>{t('columns.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {table.sorted.map((credential) => (
                    <TableRow key={credential.id}>
                      {table.columns.visibleColumns.map((column) => (
                        <TableCell
                          key={column.id}
                          className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                          style={column.cellStyle}
                        >
                          {column.cell(credential)}
                        </TableCell>
                      ))}
                      <TableCell>
                        {/* Only an active credential can be revoked — a revoked one is
                        already inert, and an expired one stops authenticating on
                        its own, so offering the action would imply otherwise. */}
                        {credential.status === 'active' ? (
                          <RequirePermission permission="backoffice:identity:mcp-access-revoke">
                            <Button size="sm" variant="danger-quiet" onClick={() => setPendingRevoke(credential)}>
                              {tRevoke('cta')}
                            </Button>
                          </RequirePermission>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        {/* The kill switch has no list to sit in when the tool read was refused,
            so it keeps a card of its own. Never both — the header renders it
            whenever the section exists. */}
        {customerSwitch !== null && tools === null && (
          <div className="flex items-center justify-end rounded-lg border border-border bg-surface px-4 py-3">
            <CustomerSurfaceState state={customerSwitch} />
          </div>
        )}
        {/* Absent, not visible-and-denied: an operator without
          `mcp-surface-read` never receives the list, so there is no section to
          hide (IMM-625.2 AC 7). */}
        {tools !== null && (
          <section aria-label={tTools('heading')} className="rounded-lg border border-border bg-surface">
            <div className="flex flex-wrap items-start justify-between gap-4 px-4 py-3">
              <div>
                <h2 className="text-base font-semibold">{tTools('heading')}</h2>
                <p className="text-base-sm text-text-secondary">{tTools('description')}</p>
              </div>
              {/* The customer kill switch, in the header of the list it actually
                  kills (IMM-625 design run). The page carries TWO switches whose
                  names differ by one word — „MCP-Zugriff deaktivieren" for the
                  operators' own credentials, „MCP-Zugang deaktivieren" for the
                  customers' — and they govern different populations. Reading the
                  axe beside the scalpel is what stops „this tool is withdrawn"
                  being confused with „the interface is off".

                  Rendered from a shared component rather than inline, because it
                  must ALSO render when the tool list does not: a 403 on the list
                  hides that section, and a kill switch that vanished with it
                  would be gone at exactly the moment somebody reached for it. */}
              {customerSwitch !== null && <CustomerSurfaceState state={customerSwitch} />}
            </div>
            {customerSwitch !== null && !customerSwitch.enabled && (
              <p
                role="alert"
                className="border-y border-danger-200 bg-danger-50 px-4 py-3 text-base-sm font-medium text-danger-700"
              >
                {tCustomer('disabled-banner')}
              </p>
            )}
            {/* Stated inside THIS section, so a fully-disabled interface is never
              mistaken for the reason an exposed tool does nothing (AC 9).
              `customerSwitch` is itself null without the read grant, which is
              not the same as the switch being off. */}
            {/* A failed read is NOT a missing grant: the section stays, and says
              the list could not be loaded, so an operator is never told by
              omission that they lack a permission they hold (BL-058). */}
            {tools.kind === 'failed' && (
              <p
                role="alert"
                className="border-y border-danger-200 bg-danger-50 px-4 py-3 text-base-sm text-danger-700"
              >
                {tTools('load-error')}
              </p>
            )}
            {customerSwitch?.enabled === false && (
              <p
                role="status"
                className="border-y border-warning-200 bg-warning-50 px-4 py-3 text-base-sm font-medium text-warning-700"
              >
                {tTools('kill-switch-notice')}
              </p>
            )}
            {/* Only a successful read has a list to filter or an emptiness to
              report — on a failed one both would be fabrications. */}
            {tools.kind === 'ready' && (
              <>
                {/* Sticky, because the catalogue is meant to grow: an operator
                  fifty rows deep must be able to narrow the list without
                  scrolling back for the controls that do it. Inside the
                  section rather than the page, so the credential tables above
                  scroll away normally. */}
                <div className="sticky top-0 z-10 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border bg-surface px-4 py-3">
                  {/* One chip set, single-select, backed by the URL — the loader
                    is what narrows the list, so „Nur ungeprüfte" can be
                    answered in the server's arrival order. */}
                  <div role="group" aria-label={tTools('filters.label')} className="flex flex-wrap items-center gap-2">
                    {McpToolExposureFilterSchema.options.map((option) => (
                      <FilterPill
                        key={option}
                        label={tTools(`filters.${option}`)}
                        active={toolFilter === option}
                        onToggle={() => selectToolFilter(option)}
                        {...(toolFilterCounts === null ? {} : { count: toolFilterCounts[option] })}
                        /* „Nur ungeprüfte" is the one an operator is meant to act
                         on; the rest are totals. */
                        countTone={option === 'unreviewed' ? 'attention' : 'neutral'}
                      />
                    ))}
                  </div>
                  {/* Submitted, not typed-through (IMM-769.7). The narrowing is
                    the server's now, so a request per keystroke would be a
                    request per keystroke; Enter (or blur) is the commit, and
                    the draft is what the operator sees meanwhile. */}
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      narrowTools((params) => {
                        const term = searchDraft.trim();

                        if (term === '') params.delete(TOOL_SEARCH_PARAM);
                        else params.set(TOOL_SEARCH_PARAM, term);
                      });
                    }}
                  >
                    <Input
                      className="w-64"
                      type="search"
                      aria-label={tTools('filters.search')}
                      placeholder={tTools('filters.search-placeholder')}
                      value={searchDraft}
                      onChange={(event) => setSearchDraft(event.target.value)}
                      onBlur={(event) => {
                        const term = event.target.value.trim();

                        if (term === toolSearch) return;

                        narrowTools((params) => {
                          if (term === '') params.delete(TOOL_SEARCH_PARAM);
                          else params.set(TOOL_SEARCH_PARAM, term);
                        });
                      }}
                    />
                  </form>
                  {/* The module facet (IMM-769.7). Its counts come with the
                    response and describe the current filter and search BEFORE
                    the module narrows, so the option an operator is about to
                    switch to is always offered with a real number. */}
                  {toolModules.length > 0 && (
                    <label className="flex items-center gap-2 text-base-sm text-text-secondary">
                      {tTools('module.label')}
                      <select
                        className="h-control-sm rounded-md border border-border bg-surface px-2 text-base-sm"
                        value={toolModule ?? ''}
                        onChange={(event) =>
                          narrowTools((params) => {
                            if (event.target.value === '') params.delete(TOOL_MODULE_PARAM);
                            else params.set(TOOL_MODULE_PARAM, event.target.value);
                          })
                        }
                      >
                        <option value="">{tTools('module.all')}</option>
                        {toolModules.map((facet) => (
                          <option key={facet.module} value={facet.module}>
                            {facet.module} ({facet.total})
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {/* What the list holds, and how much of it is live. BOTH
                    numbers are the SERVER's count of the whole narrowing — the
                    page cannot count what it was never sent (BL-053 cuts the
                    other way once the list is paged), which is why `exposed` is
                    no longer derived from the rendered rows (IMM-832.1). The
                    copy fronts it with „davon" so it binds to the tool count
                    beside it rather than to the „Nur freigegebene" chip, which
                    is defined over the search term alone. */}
                  {/* Deliberately NOT a live region: it re-renders on every
                    narrowing, and `role="status"` would queue an announcement
                    over the result the operator is filtering for. */}
                  <p className="text-base-sm text-text-tertiary tabular-nums">
                    {visibleTools.length === toolsTotal
                      ? tTools('count', { count: toolsTotal, exposed: toolsExposedTotal })
                      : tTools('count-filtered', {
                          visible: visibleTools.length,
                          total: toolsTotal,
                          exposed: toolsExposedTotal,
                        })}
                  </p>
                </div>
                {/* The batch bar, present only with a selection — an empty bar
                  would be a permanent strip of disabled buttons over a list
                  whose ordinary use is one row at a time. */}
                {selectedToolIds.size > 0 && (
                  <RequirePermission permission="backoffice:identity:mcp-surface-manage">
                    <div
                      role="group"
                      aria-label={tTools('selection.label')}
                      className="flex flex-wrap items-center gap-3 border-t border-border bg-subtle px-4 py-2"
                    >
                      <p className="text-base-sm font-medium text-text-primary">
                        {tTools('selection.count', { count: selectedToolIds.size })}
                      </p>
                      {/* Disabled, not merely quieter, when the selection
                        exposes nothing (IMM-832.2): the write is not redundant,
                        it is attributable — it would stamp a ruling nobody made
                        onto every selected tool and emit an audit event for it.
                        The LABEL changes with the weight, because a quieter
                        button of the same name is a distinction in the
                        stylesheet and not one an operator reads (BL-064). */}
                      <Button
                        size="sm"
                        variant={selectionExposesNothing ? 'secondary' : 'primary'}
                        onClick={() => setPendingBulk(true)}
                        disabled={bulkSaving || selectionExposesNothing}
                      >
                        {tTools(selectionExposesNothing ? 'already-exposed-cta' : 'expose-cta')}
                      </Button>
                      {/* The bar's own label branches too, not just the
                        confirmation behind it (IMM-774.1). An operator who will
                        not press „Freigabe entziehen" on a tool that was never
                        freigegeben never reaches the honest wording inside the
                        modal, so a truthful confirmation behind a destructive
                        entry point is a distinction that is not perceptible.
                        `secondary` rather than IMM-797.1's `danger-quiet` in
                        that case: a red tint, however quiet, still says
                        „something is being taken away". */}
                      <Button
                        size="sm"
                        variant={selectionTakesNothingAway ? 'secondary' : 'danger-quiet'}
                        onClick={() => setPendingBulk(false)}
                        disabled={bulkSaving}
                      >
                        {tTools(selectionTakesNothingAway ? 'keep-closed-cta' : 'withdraw-cta')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedToolIds(new Set())}
                        disabled={bulkSaving}
                      >
                        {tTools('selection.clear')}
                      </Button>
                    </div>
                  </RequirePermission>
                )}
                {bulkError !== null && pendingBulk === null && (
                  <p role="alert" className="px-4 pb-3 text-base-sm text-danger-600">
                    {bulkError}
                  </p>
                )}
                {entryRemoved && (
                  <p role="status" className="px-4 pb-3 text-base-sm text-text-secondary">
                    {tTools('stale.removed-confirmation')}
                  </p>
                )}
                {toolError !== null && pendingWithdraw === null && pendingRemoval === null && (
                  <p role="alert" className="px-4 pb-3 text-base-sm text-danger-600">
                    {toolError}
                  </p>
                )}
                {visibleTools.length === 0 ? (
                  <div className="p-6">
                    <EmptyState
                      title={tTools(`${toolsEmptyCopy}.heading`)}
                      description={tTools(`${toolsEmptyCopy}.description`)}
                    />
                  </div>
                ) : (
                  <ul className="divide-y divide-border border-t border-border">
                    {visibleTools.map((tool) => {
                      // The ONE membership test over the status enum (BL-049).
                      // A stale row is an exposure entry whose tool has left the
                      // catalogue: it keeps its identifier and nothing else, so
                      // every describing field must branch on this rather than
                      // assert a value the wire deliberately nulled.
                      const catalogued = mcpToolIsCatalogued(tool.status);

                      const expanded = expandedToolId === tool.toolId;

                      return (
                        <li key={tool.toolId}>
                          <div className="flex flex-wrap items-center gap-3 px-4 py-2">
                            {/* Only a catalogued row can join a batch: a stale
                              entry has no tool to rule on, and naming one
                              refuses the WHOLE batch. Rendered as a spacer
                              rather than omitted so the rows stay aligned. */}
                            <RequirePermission permission="backoffice:identity:mcp-surface-manage">
                              {catalogued ? (
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 shrink-0 rounded border-border"
                                  checked={selectedToolIds.has(tool.toolId)}
                                  onChange={() => toggleToolSelected(tool.toolId)}
                                  aria-label={catalogued && tool.name !== null ? tool.name : tool.toolId}
                                />
                              ) : (
                                <span className="h-4 w-4 shrink-0" aria-hidden />
                              )}
                            </RequirePermission>
                            {/* The whole leading half is the disclosure control:
                              the collapsed row carries only what an operator
                              scans for — what the tool is, whether it is new,
                              whether it is exposed — and everything they read
                              one row at a time lives behind this toggle. */}
                            <button
                              type="button"
                              aria-expanded={expanded}
                              aria-controls={`tool-details-${tool.toolId}`}
                              onClick={() => {
                                setExpandedToolId(expanded ? null : tool.toolId);
                                // Closing a row, or opening another, retires its
                                // verdict: the panel is unmounted either way, and
                                // a diagnosis left armed would re-fetch the moment
                                // the row came back.
                                setDiagnosedToolId(null);
                              }}
                              className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left"
                            >
                              <ChevronDownIcon
                                className={`h-4 w-4 shrink-0 text-text-tertiary transition-transform ${
                                  expanded ? '' : '-rotate-90'
                                }`}
                                aria-hidden
                              />
                              {/* One line, not two: name, identifier and state
                                read left to right, so a screenful of rows is a
                                screenful of tools rather than half as many. */}
                              <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                                {/* Where the tool's name would be, a stale row
                                  says what it actually is — the first thing an
                                  operator reads is the one that has to carry
                                  it. */}
                                <span className="truncate font-medium text-text-primary">
                                  {catalogued ? tool.name : tTools('stale.notice')}
                                </span>
                                <span className="truncate font-mono text-base-sm text-text-tertiary">
                                  {tool.toolId}
                                </span>
                                {/* Never on a stale row: „noch nicht geprüft"
                                  would invite a ruling on a tool that is
                                  gone. */}
                                {catalogued && !tool.reviewed && (
                                  <StatusBadge tone="info" size="sm" label={tTools('unreviewed')} />
                                )}
                                {/* An exposure verdict on a stale row would be a
                                  live claim about a tool the same row says is
                                  gone: the entry may still carry exposed=true,
                                  but nothing can reach it, because both the
                                  catalogue projection and dispatch are driven by
                                  the catalogue rather than by this table. */}
                                {catalogued && !canManageSurface && (
                                  <StatusBadge
                                    tone={tool.exposed ? 'success' : 'neutral'}
                                    size="sm"
                                    label={tool.exposed ? tTools('exposure.exposed') : tTools('exposure.withdrawn')}
                                  />
                                )}
                              </span>
                            </button>
                            <div className="flex shrink-0 items-center gap-2">
                              <RequirePermission permission="backoffice:identity:mcp-surface-manage">
                                {/* A stale entry has no exposure to rule on — the
                                  only honest action is clearing the row, and it
                                  confirms first like every other removal. */}
                                {!catalogued ? (
                                  <Button
                                    size="sm"
                                    variant="danger-quiet"
                                    disabled={savingToolIds.has(tool.toolId)}
                                    onClick={() => {
                                      setToolError(null);
                                      setEntryRemoved(false);
                                      setPendingRemoval(tool);
                                    }}
                                  >
                                    {tTools('stale.remove-cta')}
                                  </Button>
                                ) : (
                                  /* Exposing is one click: an incident is the wrong
                                    moment to add a step, and exposure is not the
                                    dangerous direction. Withdrawal confirms first,
                                    and the switch stays on until it is confirmed —
                                    an "off" over a still-reachable tool would be a
                                    false claim for the length of the dialog. */
                                  <>
                                    <Switch
                                      checked={tool.exposed}
                                      label={tTools('exposure.switch-label', { name: tool.name ?? tool.toolId })}
                                      disabled={savingToolIds.has(tool.toolId)}
                                      onCheckedChange={(next) => {
                                        if (next) {
                                          void saveToolExposure(tool, true);
                                        } else {
                                          setToolError(null);
                                          setPendingWithdraw(tool);
                                        }
                                      }}
                                    />
                                    <span
                                      className={`whitespace-nowrap text-base-sm ${
                                        tool.exposed ? 'text-text-primary' : 'text-text-tertiary'
                                      }`}
                                    >
                                      {tool.exposed ? tTools('exposure.exposed') : tTools('exposure.withdrawn')}
                                    </span>
                                    {/* The second ruling, beside the switch
                                      rather than on it (IMM-774.1, BL-101). A
                                      switch is a two-way branch on the very
                                      field it writes — it can only ever offer
                                      the OTHER direction, and you cannot flip
                                      „off" one that is already off. A
                                      never-reviewed tool that is closed has
                                      „geprüft, und bewusst nicht freigegeben"
                                      with no call site on the switch at all,
                                      whatever the switch is
                                      shaped like. It writes `exposed: false`
                                      over `false`: the exposure does not move,
                                      the operator's RULING is what gets
                                      recorded, and the backend clears
                                      `reviewed` on a decision in either
                                      direction.
                                      No confirmation, unlike switching a tool
                                      off — nothing is taken away, so the
                                      withdraw modal's „Alle Workspaces verlieren
                                      damit den Zugriff" would be a lie in front
                                      of a write that changes nobody's access.
                                      Quiet `secondary`, never `danger-quiet`:
                                      recording a review removes nothing, and
                                      IMM-797.1 reserves even the tinted red for
                                      the destructive direction. Rendered only
                                      while the marker is still there and the
                                      tool is still closed — on a ruled row it
                                      would be a no-op, and beside an ON switch
                                      it would contradict the state word next to
                                      it. Since IMM-1094.6 an arrival is
                                      `exposed && !reviewed`; `!tool.exposed`
                                      keeps this button off an open row, where
                                      „nicht freigeben" would contradict the
                                      state word beside it. An open, unreviewed
                                      tool is reviewed through the bulk
                                      „Freigeben" or withdrawn through the
                                      switch. */}
                                    {!tool.exposed && !tool.reviewed && (
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        disabled={savingToolIds.has(tool.toolId)}
                                        onClick={() => saveToolExposure(tool, false)}
                                      >
                                        {tTools('keep-closed-cta')}
                                      </Button>
                                    )}
                                  </>
                                )}
                              </RequirePermission>
                            </div>
                          </div>
                          {/* Inline rather than a separate page: the details are
                            read against the badges one line above them, and the
                            diagnosis panel is about THIS tool. One open row at a
                            time — two verdicts about two tools is exactly the
                            confusion the panel exists to remove. */}
                          {/* The container is ALWAYS rendered and hidden when
                            collapsed, so the button's `aria-controls` never
                            points at an element that is not there — a dangling
                            reference is dropped silently by every screen reader
                            that follows it. Its CONTENTS stay conditional: the
                            diagnosis panel fetches on mount, so rendering it for
                            every row is exactly the cost this disclosure exists
                            to avoid. */}
                          <div id={`tool-details-${tool.toolId}`} hidden={!expanded} className="bg-subtle">
                            {expanded && (
                              <>
                                <dl className="grid gap-x-8 gap-y-3 px-4 py-4 pl-11 sm:grid-cols-2">
                                  <ToolDetail label={tTools('details.description')}>
                                    {catalogued ? tool.description : tTools('none')}
                                  </ToolDetail>
                                  <ToolDetail label={tTools('details.kind')}>
                                    {catalogued ? tTools(`kind.${tool.kind}`) : tTools('none')}
                                  </ToolDetail>
                                  {/* A read tool has no side-effect class at all — an
                                  em dash says so, where an empty field reads as a
                                  missing value. */}
                                  <ToolDetail label={tTools('details.side-effect')}>
                                    {tool.sideEffectClass === null
                                      ? tTools('none')
                                      : tTools(`side-effect.${tool.sideEffectClass}`)}
                                  </ToolDetail>
                                  <ToolDetail label={tTools('details.permission')} mono>
                                    {catalogued ? tool.requiredPermission : tTools('none')}
                                  </ToolDetail>
                                  {/* Both provenance fields are null for a tool nobody
                                  has ruled on yet — the page's own fallbacks,
                                  never "null". */}
                                  <ToolDetail label={tTools('details.changed-by')}>
                                    {tool.changedByAdminId ?? tTools('none')}
                                  </ToolDetail>
                                  <ToolDetail label={tTools('details.changed-at')}>
                                    {formatTimestamp(tool.changedAt)}
                                  </ToolDetail>
                                </dl>
                                {/* Outside the manage gate on purpose: the
                                  diagnosis runs on `mcp-surface-read`, because
                                  answering a support question is not changing
                                  anything. A stale entry names a tool the
                                  catalogue no longer has, so there is nothing to
                                  diagnose. */}
                                {/* Behind its own affordance rather than mounted
                                  with the details: the panel asks the API-key
                                  list for EVERY workspace the moment it mounts,
                                  and an operator expanding rows to read their
                                  descriptions must not pay for a diagnosis they
                                  never asked for. */}
                                {/* `pl-7` on top of the panel's own `px-4`: the
                                  diagnosis lines up with the fields above it, on
                                  the indent the disclosure chevron opens. */}
                                {catalogued &&
                                  (diagnosedToolId === tool.toolId ? (
                                    <div className="border-t border-border pl-7">
                                      <ToolReachabilityPanel toolId={tool.toolId} />
                                    </div>
                                  ) : (
                                    <div className="border-t border-border px-4 py-3 pl-11">
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        onClick={() => setDiagnosedToolId(tool.toolId)}
                                      >
                                        {tReach('cta')}
                                      </Button>
                                    </div>
                                  ))}
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {/* The pager (IMM-769.7). Prev/next over an exact offset rather
                  than an accumulating „load more": the list is a page of a
                  server-ordered set, and appending pages client-side would put
                  a second, staler copy of the ordering in the browser. Hidden
                  entirely while everything fits on one page. */}
                {(toolOffset > 0 || toolsTotal > toolOffset + visibleTools.length) && (
                  <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2">
                    <p className="text-base-sm text-text-tertiary tabular-nums">
                      {tTools('page.range', {
                        from: toolOffset + 1,
                        to: toolOffset + visibleTools.length,
                        total: toolsTotal,
                      })}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={toolOffset === 0}
                        onClick={() =>
                          narrowTools(
                            (params) => {
                              const previous = Math.max(0, toolOffset - TOOL_PAGE_SIZE);

                              if (previous === 0) params.delete(TOOL_OFFSET_PARAM);
                              else params.set(TOOL_OFFSET_PARAM, String(previous));
                            },
                            { keepOffset: true },
                          )
                        }
                      >
                        {tTools('page.previous')}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={toolOffset + visibleTools.length >= toolsTotal}
                        onClick={() =>
                          narrowTools((params) => params.set(TOOL_OFFSET_PARAM, String(toolOffset + TOOL_PAGE_SIZE)), {
                            keepOffset: true,
                          })
                        }
                      >
                        {tTools('page.next')}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}
        {allCredentials !== null && (
          <section aria-label={tIncident('heading')} className="rounded-lg border border-border bg-surface">
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <h2 className="text-base font-semibold">{tIncident('heading')}</h2>
                <p className="text-base-sm text-text-secondary">{tIncident('description')}</p>
              </div>
              {accessSwitch.enabled ? (
                <Button size="sm" variant="danger-quiet" onClick={() => setPendingSwitch(false)}>
                  {tIncident('disable-cta')}
                </Button>
              ) : (
                <Button size="sm" onClick={() => setPendingSwitch(true)}>
                  {tIncident('enable-cta')}
                </Button>
              )}
            </div>
            <Table flush>
              <TableHeader>
                <TableRow>
                  {allCredentialHeads.map(({ field, label, icon }) => (
                    <TableHead
                      key={field}
                      {...(allSort.sort?.field === field ? { 'aria-sort': ARIA_SORT[allSort.sort.direction] } : {})}
                    >
                      <SortableHeader
                        field={field}
                        label={<ColumnLabel icon={icon}>{label}</ColumnLabel>}
                        ariaLabel={tTable('sort-label', { column: label })}
                        sort={allSort.sort}
                        onSort={allSort.toggleSort}
                      />
                    </TableHead>
                  ))}
                  <TableHead>{t('columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allSort.sorted.map((credential) => (
                  <TableRow key={credential.id}>
                    <TableCell>{credential.name}</TableCell>
                    <TableCell className="text-text-tertiary">{issuedByLabel(credential)}</TableCell>
                    <TableCell>{t(`status.${credential.status}`)}</TableCell>
                    <TableCell className="text-text-tertiary tabular-nums">
                      {intl.formatDate(credential.expiresAt, { dateStyle: 'short', timeStyle: 'short' })}
                    </TableCell>
                    <TableCell>
                      {credential.status === 'active' ? (
                        <Button size="sm" variant="danger-quiet" onClick={() => setPendingForeignRevoke(credential)}>
                          {tRevoke('cta')}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </section>
        )}
      </div>
      {/* The batch confirmation (IMM-769.7). BOTH directions ask, unlike the
          single row where only withdrawal does: the risk in a batch is the
          COUNT, not the direction, and „nothing was saved" only reassures an
          operator who was told the write was atomic before they ran it. */}
      {pendingBulk !== null && (
        <Modal
          open
          onClose={() => (bulkSaving ? undefined : (setPendingBulk(null), setBulkError(null)))}
          confirmOnClose={false}
          size="md"
          title={tTools(
            pendingBulk
              ? 'bulk.confirm-expose-title'
              : selectionTakesNothingAway
                ? 'bulk.confirm-keep-closed-title'
                : 'bulk.confirm-withdraw-title',
            { count: selectedToolIds.size },
          )}
          footer={
            <>
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  setPendingBulk(null);
                  setBulkError(null);
                }}
                disabled={bulkSaving}
              >
                {tTools('cancel-cta')}
              </Button>
              <Button
                type="button"
                variant={pendingBulk || selectionTakesNothingAway ? 'primary' : 'danger'}
                onClick={() => saveBulkExposure(pendingBulk)}
                disabled={bulkSaving}
              >
                {tTools(pendingBulk ? 'expose-cta' : selectionTakesNothingAway ? 'keep-closed-cta' : 'withdraw-cta')}
              </Button>
            </>
          }
        >
          {/* The body branches on the same predicate as the title and the CTA
            (IMM-774.1). „Die Änderung greift ab der nächsten Anfrage eines
            verbundenen Clients" asserts an effect this write does not have when
            nothing in the selection was exposed — nobody's catalogue moves. The
            atomicity is the half that stays true either way, so it is the half
            both strings keep. */}
          <p className="text-base-sm text-text-secondary">
            {tTools(selectionTakesNothingAway && !pendingBulk ? 'bulk.confirm-keep-closed-body' : 'bulk.confirm-body')}
          </p>
          {bulkError !== null && (
            <p role="alert" className="mt-3 text-base-sm text-danger-600">
              {bulkError}
            </p>
          )}
        </Modal>
      )}
      {pendingWithdraw !== null && (
        <Modal
          open
          onClose={() =>
            savingToolIds.has(pendingWithdraw.toolId) ? undefined : (setPendingWithdraw(null), setToolError(null))
          }
          confirmOnClose={false}
          size="md"
          title={tTools('withdraw-confirm-title')}
          footer={
            <>
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  setPendingWithdraw(null);
                  setToolError(null);
                }}
                disabled={savingToolIds.has(pendingWithdraw.toolId)}
              >
                {tTools('cancel-cta')}
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => saveToolExposure(pendingWithdraw, false)}
                disabled={savingToolIds.has(pendingWithdraw.toolId)}
              >
                {tTools('withdraw-cta')}
              </Button>
            </>
          }
        >
          {/* Named with the same expression the switch that opened this dialog
            uses for its own label (IMM-832.2) — two renderings of „which tool"
            inside one interaction is the inconsistency this is about. The
            catalogue holds ~36 near-identical identifiers and the switch is
            34×20px, so this is the last place a mis-clicked row can be caught. */}
          <p className="text-sm text-text-secondary">
            {tTools('withdraw-confirm-body', { name: pendingWithdraw.name ?? pendingWithdraw.toolId })}
          </p>
          {toolError !== null && <p className="mt-3 text-sm text-danger-600">{toolError}</p>}
        </Modal>
      )}
      {pendingRemoval !== null && (
        <Modal
          open
          onClose={() =>
            savingToolIds.has(pendingRemoval.toolId) ? undefined : (setPendingRemoval(null), setToolError(null))
          }
          confirmOnClose={false}
          size="md"
          title={tTools('stale.confirm-title')}
          footer={
            <>
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  setPendingRemoval(null);
                  setToolError(null);
                }}
                disabled={savingToolIds.has(pendingRemoval.toolId)}
              >
                {tTools('cancel-cta')}
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => removeStaleEntry(pendingRemoval)}
                disabled={savingToolIds.has(pendingRemoval.toolId)}
              >
                {tTools('stale.remove-cta')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-text-secondary">{tTools('stale.confirm-body')}</p>
          {toolError !== null && <p className="mt-3 text-sm text-danger-600">{toolError}</p>}
        </Modal>
      )}
      {pendingForeignRevoke !== null && (
        <Modal
          open
          onClose={() => (revoking ? undefined : (setPendingForeignRevoke(null), setRevokeError(null)))}
          confirmOnClose={false}
          size="md"
          title={tRevoke('confirm-title')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={() => setPendingForeignRevoke(null)} disabled={revoking}>
                {tRevoke('cancel-cta')}
              </Button>
              <Button type="button" variant="danger" onClick={confirmForeignRevoke} disabled={revoking}>
                {revoking ? tRevoke('loading') : tRevoke('confirm-cta')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-text-secondary">
            {tIncident('revoke-confirm-body', {
              name: pendingForeignRevoke.name,
              operator: pendingForeignRevoke.issuedByAdminEmail ?? pendingForeignRevoke.issuedByAdminId,
            })}
          </p>
          {revokeError !== null && <p className="mt-3 text-sm text-danger-600">{revokeError}</p>}
        </Modal>
      )}
      {pendingCustomerSwitch !== null && (
        <Modal
          open
          onClose={() =>
            customerSwitching ? undefined : (setPendingCustomerSwitch(null), setCustomerSwitchError(null))
          }
          confirmOnClose={false}
          size="md"
          title={pendingCustomerSwitch ? tCustomer('enable-cta') : tCustomer('disable-confirm-title')}
          footer={
            <>
              <Button
                variant="ghost"
                type="button"
                onClick={() => setPendingCustomerSwitch(null)}
                disabled={customerSwitching}
              >
                {tRevoke('cancel-cta')}
              </Button>
              <Button
                type="button"
                variant={pendingCustomerSwitch ? 'primary' : 'danger'}
                onClick={confirmCustomerSwitch}
                disabled={customerSwitching}
              >
                {pendingCustomerSwitch ? tCustomer('enable-cta') : tCustomer('disable-cta')}
              </Button>
            </>
          }
        >
          {!pendingCustomerSwitch && <p className="text-sm text-text-secondary">{tCustomer('disable-confirm-body')}</p>}
          {customerSwitchError !== null && <p className="mt-3 text-sm text-danger-600">{customerSwitchError}</p>}
        </Modal>
      )}
      {pendingSwitch !== null && (
        <Modal
          open
          onClose={() => (switching ? undefined : (setPendingSwitch(null), setSwitchError(null)))}
          confirmOnClose={false}
          size="md"
          title={pendingSwitch ? tIncident('enable-cta') : tIncident('disable-confirm-title')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={() => setPendingSwitch(null)} disabled={switching}>
                {tRevoke('cancel-cta')}
              </Button>
              <Button
                type="button"
                variant={pendingSwitch ? 'primary' : 'danger'}
                onClick={confirmSwitch}
                disabled={switching}
              >
                {pendingSwitch ? tIncident('enable-cta') : tIncident('disable-confirm-cta')}
              </Button>
            </>
          }
        >
          {!pendingSwitch && <p className="text-sm text-text-secondary">{tIncident('disable-confirm-body')}</p>}
          {switchError !== null && <p className="mt-3 text-sm text-danger-600">{switchError}</p>}
        </Modal>
      )}
      {pendingRevoke !== null && (
        <Modal
          open
          onClose={closeConfirm}
          confirmOnClose={false}
          size="md"
          title={tRevoke('confirm-title')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={closeConfirm} disabled={revoking}>
                {tRevoke('cancel-cta')}
              </Button>
              <Button type="button" variant="danger" onClick={confirmRevoke} disabled={revoking}>
                {revoking ? tRevoke('loading') : tRevoke('confirm-cta')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-text-secondary">{tRevoke('confirm-body', { name: pendingRevoke.name })}</p>
          {revokeError !== null && <p className="mt-3 text-sm text-danger-600">{revokeError}</p>}
        </Modal>
      )}
      <Outlet />
    </>
  );
}

/**
 * One field of an expanded tool row.
 *
 * A `<dt>`/`<dd>` pair rather than a column: the label travels WITH its value,
 * so a field that is an em dash still says what it is missing — which a
 * headerless list cell could not.
 */
function ToolDetail({
  label,
  mono = false,
  children,
}: {
  readonly label: string;
  readonly mono?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-sm font-medium text-text-tertiary">{label}</dt>
      <dd className={`text-base-sm text-text-secondary${mono ? ' font-mono' : ''}`}>{children}</dd>
    </div>
  );
}
